import React from "react";
import { useDispatch, useSelector } from "react-redux";
//#region redux
import { AppState } from "store";
import { sentSupportCase } from "store/actions/diagnosticsActions";
//#endregion
//#region types
import { AgentReply, AgentTurn, SupportCaseContent } from "models/agent/agentContract";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import {
  lastAgentTurn,
  modeOf,
  pickedChoiceOf,
  questionAttachments,
  AgentMode,
  ReplyRoute,
  routeOf,
  sendRecovering,
} from "./agentFlow";
//#endregion
//#region components
import useCurrentAgentConversation from "./useCurrentAgentConversation";
import { citedFaqArticles } from "./agentFaqLinks";
import {
  buildSupportCasePost,
  caseDetailRows,
  firstQuestionText,
  localCaseContent,
  summarizedCaseContent,
  toConversationLines,
} from "./supportCaseContent";
import useAgentService, { AgentServiceError } from "serviceHooks/useAgentService";
import { useSupportService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import * as Sentry from "@sentry/react";

interface AgentQuestionOptions {
  /** The agent is switched off on the server: the popper falls back to the FAQ, as freshly opened. */
  onUnavailable: () => void;
  onOpenCase: (caseId: number) => void;
}

/** Session creation answered 404: the agent is switched off (or not deployed). */
class AgentUnavailableError extends Error {}

const t = LocalizationHelperInstance.getFormattedMessage;

const errorStatus = (error: unknown) =>
  typeof (error as { status?: unknown })?.status === "number" ? (error as { status: number }).status : undefined;

const useAgentQuestion = ({ onUnavailable, onOpenCase }: AgentQuestionOptions) => {
  const agentService = useAgentService();
  const supportService = useSupportService();
  const dispatch = useDispatch();
  const supportCategories = useSelector((appState: AppState) => appState.common.supportCategories);
  const language = useSelector((appState: AppState) => appState.common.locale.language);
  const { conversation, state } = useCurrentAgentConversation();
  // Layout effect, so an expired conversation is reset before the old state paints.
  React.useLayoutEffect(() => conversation.open(), [conversation]);

  const { push } = conversation;
  const mode = modeOf(state);
  const afterAnswer = state.turns.some(
    (turn) => turn.who === "agent" && turn.reply && routeOf(turn.reply) === "answered"
  );
  const files = questionAttachments(state.turns, conversation.attachmentsOf);

  // Created with the first message, not on open: most opens never write anything.
  const ensureSession = async (fresh = false): Promise<string> => {
    const { sessionId } = conversation.getSnapshot();
    if (sessionId && !fresh) return sessionId;

    try {
      const created = await agentService.createSession();
      conversation.setSessionId(created.sessionId);
      return created.sessionId;
    } catch (error) {
      // 404 here means the agent is off; on a message it means the session was forgotten.
      if (error instanceof AgentServiceError && error.status === 404) throw new AgentUnavailableError();
      throw error;
    }
  };

  const requestCaseSummary = (sessionId: string, turns: AgentTurn[]) =>
    agentService.summarizeCase(sessionId, { conversation: toConversationLines(turns), language });

  const caseSummaryKey = (sessionId: string, turns: AgentTurn[]) =>
    JSON.stringify([sessionId, toConversationLines(turns)]);

  // Asked for once the answer is shown, so a case she sends after reading it does not wait for the summary. Not
  // sooner: the summary is written only from the conversation lines sent, and the answer is not in them until then.
  const startCaseSummary = () => {
    const { sessionId, turns } = conversation.getSnapshot();
    if (!sessionId) return;
    const key = caseSummaryKey(sessionId, turns);
    if (!conversation.caseSummaryFor(key)) conversation.keepCaseSummary(key, requestCaseSummary(sessionId, turns));
  };

  const caseContent = async (): Promise<SupportCaseContent> => {
    const { sessionId, turns } = conversation.getSnapshot();
    if (!sessionId) return localCaseContent(turns);
    try {
      const summary = await (conversation.caseSummaryFor(caseSummaryKey(sessionId, turns)) ??
        requestCaseSummary(sessionId, turns));
      return summarizedCaseContent(turns, summary);
    } catch {
      return localCaseContent(turns);
    }
  };

  const prepareReview = async () => {
    conversation.setPending("prepare");
    const request = conversation.beginRequest();
    const content = await caseContent();
    if (!conversation.isCurrentRequest(request)) return;
    conversation.reviewReady(content);
    conversation.track("review.shown", { drafted: content.drafted });
  };

  const sendToSupport = async (from: AgentMode, retry: boolean, afterAnswer: boolean) => {
    const snapshot = conversation.getSnapshot();
    const attached = questionAttachments(snapshot.turns, conversation.attachmentsOf);

    conversation.setPending("send");
    const content = snapshot.review ?? (await caseContent());
    // The agent's side reaches support as a comment Core posts from the session's log. A question that moved to a
    // new session has no one log holding all of it, so it goes without a session.
    const unlinkedReason = snapshot.sessionRestarted ? "restarted" : !snapshot.sessionId ? "none" : undefined;
    const sessionId = unlinkedReason ? undefined : (snapshot.sessionId ?? undefined);
    const post = buildSupportCasePost(content);
    const categoryId = supportCategories.find((category) => category.name === content.category)?.id;
    try {
      // Errors are not shown by the client: a failure is told in the popper instead of a toast.
      const caseId = await supportService.createTicket(
        {
          title: post.subject,
          message: post.message,
          categoryId,
          attachments: attached as WintSuperkollAPIModel.SupportAttachment[],
          supportAgentSessionId: sessionId,
        },
        false
      );
      // Counted like a case from the support form, so the FAQ diagnostics and support numbers include it.
      dispatch(sentSupportCase());
      Sentry.captureMessage("Support team: Support case submitted", {
        level: "info",
        tags: {
          source: "support-agent",
          categoryId: String(categoryId ?? ""),
          hasAttachments: String(attached.length > 0),
        },
      });
      conversation.caseSent({
        caseId,
        details: caseDetailRows(
          content,
          attached.map((file) => file.fileName),
          t
        ),
      });
      conversation.track("support.sent", {
        caseId,
        from,
        retry,
        afterAnswer,
        drafted: content.drafted,
        attachments: attached.length,
        linked: !!sessionId,
        unlinkedReason,
      });
    } catch (error) {
      conversation.track("error.shown", { step: "caseSend", status: errorStatus(error) });
      conversation.caseFailed(content);
      // A direct send that fails puts the case card in front of her for the first time.
      if (!snapshot.review) conversation.track("review.shown", { drafted: content.drafted, sendFailed: true });
    }
  };

  const showReply = (reply: AgentReply): ReplyRoute => {
    const actionId = reply.kind === "card" ? reply.actionId : undefined;
    if (actionId) conversation.noteTracking({ lastActionId: actionId });
    if (reply.kind === "answer") conversation.noteTracking({ lastOutcome: reply.outcome });
    // The outcome as the server sent it, so a new one shows in the logs before the client knows it.
    conversation.track("reply.shown", {
      kind: reply.kind,
      actionId,
      outcome: reply.kind === "answer" ? reply.outcome : undefined,
      sessionEnded: reply.kind === "answer" ? reply.sessionEnded : undefined,
      faqCited: reply.kind === "answer" ? citedFaqArticles(reply.text).join(",") || undefined : undefined,
    });
    push({ who: "agent", reply });
    if (reply.kind === "answer" && reply.sessionEnded) conversation.endSession();
    return routeOf(reply);
  };

  const askAgent = async (text: string, attached: UploadedFile[]) => {
    // Read before her turn is pushed: an answer to the agent's question cannot be resent on its own.
    const answeringQuestion = modeOf(conversation.getSnapshot()) === "clarify";

    const questionTurnId = push({ who: "customer", text });
    conversation.setAttachments(questionTurnId, attached);
    conversation.noteTracking({ messageCount: conversation.getSnapshot().tracking.messageCount + 1 });
    conversation.track("message.sent", { answeringQuestion, attachments: attached.length });
    conversation.setPending("message");
    const request = conversation.beginRequest();

    // The wait goes on into the next step without a gap, so the working line never flickers away.
    let toReview = false;
    try {
      const reply = await sendRecovering(
        async (fresh) =>
          agentService.sendMessage(
            await ensureSession(fresh || conversation.getSnapshot().sessionEnded),
            text,
            attached.length > 0
          ),
        (error) => error instanceof AgentServiceError && error.status === 404 && !!conversation.getSnapshot().sessionId,
        answeringQuestion,
        () => conversation.track("session.lost", { answeringQuestion })
      );
      // A new question was started meanwhile: this reply belongs to the old one.
      if (!conversation.isCurrentRequest(request)) return;
      const route = reply === "review" ? "review" : showReply(reply);
      if (route === "review") toReview = true;
      if (route === "answered") startCaseSummary();
    } catch (error) {
      if (!conversation.isCurrentRequest(request)) return;
      if (error instanceof AgentUnavailableError) {
        // Not left unanswered in a question nobody sees again.
        conversation.removeTurn(questionTurnId);
        conversation.setPending(null);
        onUnavailable();
        return;
      }
      conversation.track("error.shown", { step: "message", status: errorStatus(error) });
      push({ who: "agent", failed: true });
      toReview = true;
    } finally {
      if (conversation.isCurrentRequest(request) && !toReview) conversation.setPending(null);
    }

    if (toReview) await prepareReview();
  };

  const onAsk = (text: string, attached: UploadedFile[]) => {
    if (!conversation.getSnapshot().pending) void askAgent(text, attached);
  };

  const onAnswer = (text: string, fromChoice: boolean) => {
    if (conversation.getSnapshot().pending) return;
    const turnId = lastAgentTurn(conversation.getSnapshot().turns)?.id;
    if (fromChoice && turnId !== undefined) conversation.markChoice(turnId, text);
    conversation.track("clarification.answered", { fromChoice });
    void askAgent(text, []);
  };

  const onSkip = (reason: "none" | "skipped") => {
    if (conversation.getSnapshot().pending) return;
    conversation.track("clarification.skipped", { reason });
    void prepareReview();
  };

  const onNewQuestion = () => {
    conversation.track("question.new", { from: mode });
    conversation.newQuestion();
  };

  const onSendToSupport = () => {
    if (conversation.getSnapshot().pending) return;
    const retry = conversation.getSnapshot().sendFailed;
    conversation.track("support.clicked", { from: mode, retry, afterAnswer });
    void sendToSupport(mode, retry, afterAnswer);
  };

  // Opened first, so `case.opened` is tracked under the conversation the case came from.
  const onGoToCase = (caseId: number) => {
    onOpenCase(caseId);
    conversation.newQuestion(false);
  };

  return {
    mode,
    pending: state.pending,
    sendFailed: state.sendFailed,
    sentCase: state.sentCase,
    question: firstQuestionText(state.turns) ?? "",
    choice: pickedChoiceOf(state.turns),
    afterAnswer,
    files,
    reply: lastAgentTurn(state.turns)?.reply,
    review: state.review ?? localCaseContent(state.turns),
    onAsk,
    onAnswer,
    onSkip,
    onNewQuestion,
    onSendToSupport,
    onGoToCase,
  };
};

export default useAgentQuestion;
